/**
 * RUM beacon contract, v1. All timings are ms offsets from
 * `performance.timeOrigin` — one clock for shell and embedded-app milestones.
 * Mirrored by the `POST /v1/rum` handler in the services repo (`ui_api/rum/`);
 * keep in sync. Design: `docs/proposals/real-user-monitoring.md`.
 */

export const RumMilestone = {
  /** Root layout's first React render. */
  ShellRendered: 'shell_rendered',
  /** Authenticated layout mounted — session, account, workspace resolved. */
  Authenticated: 'authenticated',
  /** An embedded app bundle started loading (Journeys, Design Studio). */
  ChildAppLoadStart: 'child_app_load_start',
  /** The embedded app finished its initial render. */
  ChildAppRendered: 'child_app_rendered',
  /**
   * The embedded app reports itself genuinely usable (e.g. Journeys' loading
   * state ended), via the `window.__hydra__.rum` bridge. Same
   * `performance.timeOrigin` clock — the apps share the page.
   */
  ChildAppInteractive: 'child_app_interactive',
  /** TanStack Router resolved its first navigation. */
  FirstRouteSettled: 'first_route_settled',
  /**
   * A lazily-loaded Hydra page committed its first render — recorded by
   * `lazyRoute`, so every page behind it reports without opting in.
   *
   * This is the first milestone that contains any of the page's own cost.
   * `first_route_settled` fires when the router resolves, which happens while
   * the page is still suspended: its chunk has not been fetched, evaluated or
   * rendered. Measured, a 300ms regression inside a page moved none of the
   * other milestones by more than noise (FPS-3854).
   */
  RoutePageRendered: 'route_page_rendered',
  /**
   * Synthetic "the page became usable" milestone, computed at flush time —
   * never recorded directly. The strongest usable-signal the load produced:
   * `child_app_interactive ?? child_app_rendered` when a child app started
   * loading (absent if it never got there), `first_route_settled` on
   * Hydra-native pages. One histogram series that means "ready" on every
   * route.
   */
  PageReady: 'page_ready',
} as const;

export type RumMilestone = (typeof RumMilestone)[keyof typeof RumMilestone];

/** What flushed the beacon: quiet period, hard cap, or tab hidden. */
export type RumSentReason = 'quiet' | 'timeout' | 'hidden';

/** Browser navigation timing, ms from timeOrigin. Absent = unavailable/not reached. */
export type RumNavigationTiming = {
  ttfb?: number;
  dom_content_loaded?: number;
  load_event?: number;
};

/** Deployment/account context resolved at flush time (see context.ts). */
export type RumContext = {
  ui_version: string;
  env: string;
  dc: 'us' | 'eu';
  is_masq: boolean;
  account_id?: string;
};

export type RumInitialLoadPayload = RumContext & {
  v: 1;
  kind: 'initial_load';
  /** One id per page load; stitches the metric to the log line. */
  session_id: string;
  /** Matched route *pattern*, never the raw URL — it becomes a metric label. */
  route: string;
  /** Absent milestone = never happened; the beacon always sends. */
  milestones: Partial<Record<RumMilestone, number>>;
  navigation: RumNavigationTiming;
  sent_reason: RumSentReason;
};

/**
 * Milestones a single SPA transition can reach, as ms offsets from that
 * transition's own nav start (relative durations, unlike the initial-load
 * milestones' timeOrigin offsets). `settled` = the router committed and React
 * finished rendering the destination route. `ready` = the destination page
 * claimed the measurement (useRumTransitionReady) and declared its data
 * arrived — absent on unclaimed pages, where settled is all the shell sees.
 */
export type RumTransitionMilestone = 'settled' | 'ready';

/**
 * Who renders the transition's destination. `hydra` transitions carry real
 * timings; embedded-bundle values (`journeys`, `design-studio`) mark
 * shell-observed transitions whose in-app render time the shell cannot see —
 * counted, but incomplete by design. `embedded` is the fallback when the
 * bundle can't be named.
 */
export type RumTransitionOwner = 'hydra' | 'embedded' | 'journeys' | 'design-studio';

/**
 * Why the transition beacon fired: `settled` (the normal end for unclaimed
 * pages), `ready` (a claimed page reported its data arrived), `superseded`
 * (a new navigation started first), `hidden` (tab hidden mid-transition),
 * `timeout` (never resolved — or claimed but never reported ready), or
 * `embedded_sync` (an intra-embedded-app navigation the shell only observes
 * as a URL sync — no timings).
 */
export type RumTransitionSentReason = 'settled' | 'ready' | 'superseded' | 'hidden' | 'timeout' | 'embedded_sync';

/** One SPA navigation — several per page load, same session_id as the initial_load beacon. */
export type RumTransitionPayload = RumContext & {
  v: 1;
  kind: 'transition';
  /** Same id as this page load's initial_load beacon — joins transitions to their load. */
  session_id: string;
  owner: RumTransitionOwner;
  /** Matched destination route pattern — the metric label. `unknown` when the transition never settled. */
  to_route: string;
  /** Matched origin route pattern. Log-line drill-down only, never a metric label. */
  from_route: string;
  /** ms from performance.timeOrigin when the navigation started. */
  nav_start_ms: number;
  /** ms from nav start; absent = never reached. */
  milestones: Partial<Record<RumTransitionMilestone, number>>;
  sent_reason: RumTransitionSentReason;
};

/** Where in the session lifecycle a request started. */
export type RumRequestPhase = 'initial_load' | 'transition' | 'idle';

/** One settled API request, timed by the API client (phase 2, FPS-3377). */
export type RumRequestEvent = {
  /**
   * `"METHOD /path/{template}"` — the OpenAPI path template with ids
   * unsubstituted, never a resolved URL. Low-cardinality by construction.
   */
  endpoint: string;
  /** HTTP status; 0 when no response arrived (network failure, abort, timeout). */
  status: number;
  /** ms from performance.timeOrigin at request start. */
  start_ms: number;
  duration_ms: number;
  phase: RumRequestPhase;
  /** Matched route pattern the request settled on; "unknown" when none matched. */
  route: string;
};

/** Batched per-request beacon — several per page load, same session_id as the initial_load beacon. */
export type RumRequestsPayload = RumContext & {
  v: 1;
  kind: 'requests';
  /** Same id as this page load's initial_load beacon — joins requests to milestones. */
  session_id: string;
  requests: RumRequestEvent[];
  /** Events discarded after the per-page-load cap; only present when non-zero. */
  dropped?: number;
};

export type RumBeaconPayload = RumInitialLoadPayload | RumRequestsPayload | RumTransitionPayload;
